import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { usePageMeta } from '../hooks/usePageMeta';

interface CatalogData {
  categories: { category_id: string; category_description: string }[];
  types: { type_id: string; type_description: string }[];
}

interface Preferences {
  rasff_daily: boolean;
  legislation_weekly: boolean;
  major_incident_alerts?: boolean;
  product_categories: string[];
  product_types: string[];
}

export default function ManagePreferencesPage() {
  usePageMeta({ title: 'Manage preferences', noindex: true });
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [rasffDaily, setRasffDaily] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [stageAlerts, setStageAlerts] = useState(false);
  const [incidentAlerts, setIncidentAlerts] = useState(false);
  const [productTypes, setProductTypes] = useState<string[]>(['food', 'feed', 'food contact material']);
  const [productCategories, setProductCategories] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<CatalogData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const typeLabels: Record<string, string> = {
    'food': 'Food',
    'feed': 'Feed',
    'food contact material': 'Food Contact Material (FCM)',
  };

  useEffect(() => {
    if (!token) { setTokenError('No token provided.'); setLoading(false); return; }

    Promise.all([
      fetch(`${API_BASE_URL}/api/public/preferences?token=${encodeURIComponent(token)}`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/public/product-categories`).then(r => r.json()),
    ]).then(([sub, cat]) => {
      if (sub.error) { setTokenError(sub.error); return; }
      const prefs: Preferences = sub.preferences || {};
      setEmail(sub.email || '');
      setName(sub.name || '');
      setRasffDaily(prefs.rasff_daily !== false);
      setWeeklyDigest(prefs.legislation_weekly !== false);
      setStageAlerts(prefs.legislation_stage_alerts === true);
      setIncidentAlerts(prefs.major_incident_alerts === true);
      setProductTypes(prefs.product_types?.length ? prefs.product_types : ['food', 'feed', 'food contact material']);
      setProductCategories(prefs.product_categories || []);
      setCatalog(cat);
    }).catch(() => {
      setTokenError('Could not load your preferences. Please try again.');
    }).finally(() => setLoading(false));
  }, [token]);

  function toggleType(type: string) {
    setProductTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/public/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          name: name.trim() || null,
          preferences: {
            rasff_daily: rasffDaily,
            legislation_weekly: weeklyDigest,
            legislation_stage_alerts: stageAlerts,
            major_incident_alerts: incidentAlerts,
            product_categories: productCategories,
            product_types: productTypes,
          },
        }),
      });
      if (res.ok) { setSuccess(true); }
      else { const d = await res.json(); setError(d.error || 'Something went wrong.'); }
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const categoryOptions = (catalog?.categories || [])
    .filter(c => c.category_description)
    .map(c => ({ value: c.category_description, label: c.category_description }));

  if (loading) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-card" style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Loading your preferences…
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-card" style={{ textAlign: 'center' }}>
          <div className="subscribe-card__logo">FS</div>
          <h1 className="subscribe-card__title">Food Safety Digest</h1>
          <p style={{ color: 'var(--color-danger)', marginTop: 16 }}>{tokenError}</p>
          <Link to="/subscribe/form" className="subscribe-card__cta" style={{ marginTop: 20, display: 'inline-block' }}>
            Subscribe
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-card" style={{ textAlign: 'center' }}>
          <div className="subscribe-card__logo">FS</div>
          <h1 className="subscribe-card__title">Food Safety Digest</h1>
          <div className="subscribe-success">
            <div className="subscribe-success__icon">&#10003;</div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-primary)', margin: '12px 0 8px' }}>
              Preferences saved!
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
              Your notification settings have been updated for <strong>{email}</strong>.
            </p>
            <Link to="/rasff" className="subscribe-card__cta" style={{ marginTop: 20, display: 'inline-block' }}>
              Explore the dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscribe-page">
      <div className="subscribe-card">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div className="subscribe-card__logo">FS</div>
          <h1 className="subscribe-card__title">Manage preferences</h1>
          <p className="subscribe-card__tagline" style={{ fontSize: '0.85rem' }}>{email}</p>
        </div>

        <form onSubmit={handleSubmit} className="subscribe-form">
          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Name <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(optional)</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              className="subscribe-form__input"
            />
          </div>

          <div className="subscribe-form__field">
            <label className="subscribe-form__label">Notifications</label>
            <div className="subscribe-form__checks">
              <label className="subscribe-form__check">
                <input type="checkbox" checked={rasffDaily} onChange={e => setRasffDaily(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Daily RASFF digest</span>
                  <span className="subscribe-form__check-desc">All new RASFF notifications from the past 24 hours</span>
                </div>
              </label>
              <label className="subscribe-form__check">
                <input type="checkbox" checked={weeklyDigest} onChange={e => setWeeklyDigest(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Weekly food safety digest</span>
                  <span className="subscribe-form__check-desc">Weekly briefing covering legislation, RASFF, recalls &amp; scientific updates</span>
                </div>
              </label>
              <label className="subscribe-form__check">
                <input type="checkbox" checked={stageAlerts} onChange={e => setStageAlerts(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Legislation stage alerts</span>
                  <span className="subscribe-form__check-desc">Same-day email when a high-impact draft EU act is voted, enters scrutiny, is adopted or is published</span>
                </div>
              </label>
              <label className="subscribe-form__check">
                <input type="checkbox" checked={incidentAlerts} onChange={e => setIncidentAlerts(e.target.checked)} />
                <div>
                  <span className="subscribe-form__check-label">Food safety crisis alerts</span>
                  <span className="subscribe-form__check-desc">Same-day email when a food safety incident crosses the major threshold — confirmed outbreaks, facility closures, multi-country recalls. A handful per year.</span>
                </div>
              </label>
            </div>
          </div>

          {rasffDaily && (
            <>
              <div className="subscribe-form__field">
                <label className="subscribe-form__label">Product types</label>
                <div className="subscribe-form__checks subscribe-form__checks--row">
                  {Object.entries(typeLabels).map(([val, lbl]) => (
                    <label key={val} className="subscribe-form__check subscribe-form__check--inline">
                      <input type="checkbox" checked={productTypes.includes(val)} onChange={() => toggleType(val)} />
                      <span>{lbl}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="subscribe-form__field">
                <label className="subscribe-form__label">
                  Product categories
                  <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)', fontSize: '0.8rem', marginLeft: 6 }}>
                    Leave empty for all categories
                  </span>
                </label>
                <MultiSelectFilter
                  options={categoryOptions}
                  selected={productCategories}
                  onChange={setProductCategories}
                  placeholder="All categories"
                />
              </div>
            </>
          )}

          {error && <div className="subscribe-form__error">{error}</div>}

          <button type="submit" className="subscribe-card__cta" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save preferences'}
          </button>
        </form>
      </div>
    </div>
  );
}
